import { _isNil, _notNil } from '@/littledash';
import type {
  SendIncompleteReason,
  SendReportCounts,
  SendResultReport,
  SendRuleOutcome,
  SendVerdictTally,
} from '@/model/Agent.model';

/** How each reason a check did not complete, or a rule did not apply, reads to a reviewer. */
export const REASON_TEXT: Record<SendIncompleteReason, string> = {
  binding_unresolved: 'The codelist a value is checked against could not be determined',
  cardinality_limit_reached: 'Stopped at a size limit before every record was compared',
  dataset_not_uploaded: 'Not applicable to this upload: no dataset it applies to was uploaded',
  evaluation_error: 'The check could not be run',
  implementation_absent: 'Not checked by this version of the validator yet',
  input_unavailable_in_profile: 'Needs information a CSV upload does not carry',
  insufficient_precision: 'A date is recorded too imprecisely to compare',
  not_determinable: 'No validator can check this from a submission',
  out_of_scope_version: 'The declared SEND version is not one this validator checks',
  required_domain_missing: 'A dataset the check needs was not uploaded',
  scope_unknown: 'The SEND version could not be determined',
  scope_use_case: "Doesn't apply to SEND",
  value_uninterpretable: 'A value could not be read the way the check needs',
  variable_absent: 'A column the rule reads is not in the file',
  variable_not_defined_for_domain: 'Not applicable: SENDIG defines no such variable for this dataset',
};

/**
 * Why a rule that ran was left partly unchecked, as its partial result's reasons read to a reviewer.
 * Worded for the part left out: the rest of the rule was checked, and may have passed or failed.
 */
const PARTIAL_REASON_TEXT: Partial<Record<SendIncompleteReason, string>> = {
  binding_unresolved: 'For some values, the codelist to check against could not be determined',
  cardinality_limit_reached: 'A file was too large to check in full',
  implementation_absent: 'Part of the rule is not checked by this version of the validator yet',
  insufficient_precision: 'Some dates are recorded too imprecisely to compare',
  required_domain_missing: 'A dataset part of the rule needs was not uploaded',
  value_uninterpretable: 'Some values could not be read the way the rule needs',
  variable_absent: 'A file is missing a column the rule reads, so that file was not checked',
};

/** How a check covering only part of a rule is labelled, on the rule and on its findings. */
export const PARTIAL_CHECK = 'Partial check';

/** A rule the upload was checked against only in part, and why. */
export interface SendPartialRule {
  ruleId: string;
  reason: string;
}

/**
 * Whether a rule was checked only in part: its check covers only part of the rule, or it ran and did
 * not finish. A rule no check ran for is not here — it is counted as not evaluated.
 */
const isCheckedPartially = (outcome: SendRuleOutcome): boolean =>
  outcome.implementation === 'proxy' || outcome.result.evaluation_status === 'partial';

/**
 * Why a rule was checked only in part: the reason the server gives for the rule where it has one,
 * otherwise what stopped this run.
 */
const partialReasonOf = (outcome: SendRuleOutcome): string =>
  _notNil(outcome.reason)
    ? outcome.reason
    : outcome.result.reasons.map((reason) => PARTIAL_REASON_TEXT[reason] ?? REASON_TEXT[reason] ?? reason).join('; ');

/** The published rules the upload was checked against only in part, in the server's order. */
export const partialRulesOf = (report: SendResultReport): Array<SendPartialRule> =>
  (report.rule_outcomes ?? []).reduce<Array<SendPartialRule>>((partial, outcome) => {
    if (isCheckedPartially(outcome)) {
      partial.push({ ruleId: outcome.rule_id, reason: partialReasonOf(outcome) });
    }
    return partial;
  }, []);

/**
 * The rules whose check covers only part of the rule, published or not, so their findings can carry
 * the same label.
 */
export const proxyRulesOf = (report?: SendResultReport | null): ReadonlySet<string> =>
  [...(report?.rule_outcomes ?? []), ...(report?.encoding_outcomes ?? [])].reduce<Set<string>>((proxies, outcome) => {
    if (outcome.implementation === 'proxy') {
      proxies.add(outcome.rule_id);
    }
    return proxies;
  }, new Set());

/** What the rules made of one file, read from the server's tally for its dataset. */
export interface SendFileRuleCheck {
  /** Every rule for the file finished with an exact pass, or does not apply to it. */
  exactPass: boolean;
  /** Rules that failed for the file, whether or not its own findings carry them. */
  failed: number;
  notEvaluated: number;
  /** Rules that passed without an exact, finished check. */
  checkedPartially: number;
  /** The file's checks that said they could not decide something. */
  undecided: number;
}

/**
 * The file's rule check, from the dataset's row of the server's tally and the file's exact counts.
 * Null for a result stored before either existed, which cannot say whether every rule passed.
 */
export const fileRuleCheckOf = (
  report: SendResultReport | null | undefined,
  domainCode: string,
  counts: SendReportCounts | null | undefined
): SendFileRuleCheck | null => {
  const tally: SendVerdictTally | undefined = report?.coverage_grid?.by_domain.find(
    (row) => row.key === domainCode
  )?.tally;
  const undecided = counts?.undecided_checks;
  if (_isNil(tally) || _isNil(undecided)) {
    return null;
  }
  return {
    exactPass: tally.total > 0 && tally.conforming + tally.not_applicable === tally.total && undecided === 0,
    failed: tally.failed,
    notEvaluated: tally.not_evaluated,
    checkedPartially: tally.passed - tally.conforming,
    undecided,
  };
};
